import { Search } from 'lucide-react';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * AURORA DS — paleta poleceń (⌘K) z prototypu Aurora DS.html.
 * Czysta nawigacja: filtruje przekazane pozycje menu i skacze do trasy.
 * Style: .scrim/.palette z aurora/components.css.
 */
export interface PaletteItem {
  name: string;
  href: string;
  icon?: React.ComponentType<{ className?: string }>;
  section?: string;
}

export function CommandPalette({
  items,
  open,
  onClose,
}: {
  items: PaletteItem[];
  open: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  // a11y: element, który otworzył paletę (np. przycisk .cmdk) — fokus wraca po zamknięciu
  const returnFocusRef = useRef<HTMLElement | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => i.name.toLowerCase().includes(q));
  }, [items, query]);

  useEffect(() => {
    if (open) {
      returnFocusRef.current = document.activeElement as HTMLElement | null;
      setQuery('');
      setActive(0);
      // focus po wyrenderowaniu overlaya
      setTimeout(() => inputRef.current?.focus(), 30);
    } else if (returnFocusRef.current) {
      returnFocusRef.current.focus?.();
      returnFocusRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  const go = (href: string) => {
    onClose();
    navigate(href);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter' && filtered[active]) {
      e.preventDefault();
      go(filtered[active].href);
    }
  };

  if (!open) return null;

  return (
    <>
      <div className="scrim show" onClick={onClose} aria-hidden="true" />
      <div className="palette show" role="dialog" aria-modal="true" aria-label="Paleta poleceń">
        <div className="palette__search">
          <Search />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Skok do strony, modułu…"
            aria-label="Szukaj"
            role="combobox"
            aria-expanded="true"
            aria-controls="cmdk-list"
            aria-autocomplete="list"
            aria-activedescendant={filtered.length > 0 ? `cmdk-opt-${active}` : undefined}
          />
          <kbd className="mono text-[11px] px-1.5 py-0.5 rounded border border-aurora-border bg-aurora-surface text-aurora-subtle">
            Esc
          </kbd>
        </div>
        <div className="palette__list" role="listbox" id="cmdk-list">
          {filtered.length === 0 && (
            <div className="px-3 py-4 text-sm text-aurora-subtle">Brak wyników</div>
          )}
          {filtered.map((item, i) => (
            // Nagłówek grupy (.palette__sec jak w prototypie) przed pierwszą pozycją
            // sekcji — pozycje przychodzą pogrupowane (flatMap po grupach nawigacji),
            // a filtr zachowuje kolejność, więc indeksy/klawiatura bez zmian.
            <Fragment key={item.href + item.name}>
              {item.section && item.section !== filtered[i - 1]?.section && (
                <div className="palette__sec" role="presentation">
                  {item.section}
                </div>
              )}
              <div
                id={`cmdk-opt-${i}`}
                className={`palette__item ${i === active ? 'active' : ''}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => go(item.href)}
                role="option"
                aria-selected={i === active}
              >
                {item.icon && <item.icon className="w-[18px] h-[18px]" />}
                <span>{item.name}</span>
              </div>
            </Fragment>
          ))}
        </div>
      </div>
    </>
  );
}

/** Globalny skrót ⌘K / Ctrl+K — zwraca [open, setOpen]. */
export function useCommandPalette(): [boolean, (v: boolean) => void] {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return [open, setOpen];
}
